import { useState, useRef, useEffect } from "react";
import { fetchGroupChatHasHistory } from "../utils/api/fetchGroupChatHasHistory";
import { MESSAGE_LIST_SETTLE_MS } from "../utils/chat/messageRetention";

type CheckedHasHistory = {
  channelUrl: string;
  messageId: number | undefined;
};

type UseGroupChatHasHistoryResult = {
  hasHistory: boolean;
  channelId: number | null;
};

/** has_history をスキップしてよいか（同一チャンネル＋同一アンカーなら取得済み） */
const shouldSkipHasHistoryFetch = (
  checked: CheckedHasHistory | null,
  channelUrl: string,
  anchorMessageId: number | undefined,
): boolean => {
  // チャンネルまたはアンカーが変わったら再取得
  return (
    checked?.channelUrl === channelUrl &&
    checked.messageId === anchorMessageId
  );
};

/**
 * チャンネル同期後、messages が一定時間安定してから has_history を呼ぶ。
 * （Sendbird が短時間でリストを差し替えるため、2回目リストの messageId を使う）
 */
export const useGroupChatHasHistory = ({
  channelUrl,
  isChannelSynced,
  loading,
  cutoff,
  messagesFingerprint,
  anchorMessageId,
}: {
  channelUrl: string | null;
  isChannelSynced: boolean;
  loading: boolean;
  cutoff: number;
  messagesFingerprint: string;
  anchorMessageId: number | undefined;
}): UseGroupChatHasHistoryResult => {
  const [hasHistory, setHasHistory] = useState(false);
  const [channelId, setChannelId] = useState<number | null>(null);
  const checkedRef = useRef<CheckedHasHistory | null>(null);

  useEffect(() => {
    setHasHistory(false);
    setChannelId(null);
    checkedRef.current = null;
  }, [channelUrl]);

  useEffect(() => {
    if (!channelUrl || !isChannelSynced || loading) {
      return undefined;
    }

    if (
      shouldSkipHasHistoryFetch(
        checkedRef.current,
        channelUrl,
        anchorMessageId,
      )
    ) {
      return undefined;
    }

    const fetchChannelUrl = channelUrl;
    const fetchMessageId = anchorMessageId;
    const lastCreatedAt = new Date(cutoff).toISOString();

    // fingerprint が変わるたびにリセットされ、安定後のリスト（実質2回目）で呼ぶ
    const timer = window.setTimeout(() => {
      checkedRef.current = {
        channelUrl: fetchChannelUrl,
        messageId: fetchMessageId,
      };

      fetchGroupChatHasHistory({
        channel_url: fetchChannelUrl,
        last_created_at: lastCreatedAt,
        ...(fetchMessageId !== undefined
          ? { last_sendbird_message_id: fetchMessageId }
          : {}),
      })
        .then((res) => {
          if (checkedRef.current?.channelUrl !== fetchChannelUrl) {
            return;
          }
          setHasHistory(Boolean(res.isSuccess && res.result?.has_history));
          setChannelId(
            res.isSuccess && res.result ? res.result.channel_id : null,
          );
        })
        .catch(() => {
          if (checkedRef.current?.channelUrl === fetchChannelUrl) {
            setHasHistory(false);
            setChannelId(null);
          }
        });
    }, MESSAGE_LIST_SETTLE_MS);

    return () => window.clearTimeout(timer);
  }, [
    channelUrl,
    isChannelSynced,
    loading,
    cutoff,
    messagesFingerprint,
    anchorMessageId,
  ]);

  return { hasHistory, channelId };
};
